import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePhones } from "@/lib/contacts";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, Phone, Mail, ArrowRight, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LanguageSelector } from "@/components/common/LanguageSelector";
import { SERVICES } from "@/lib/services";

const linkBase =
  "text-sm font-medium px-3 py-2 rounded-lg text-slate-200 hover:text-white transition-colors";
const linkActive = "text-white";

export const Navbar = () => {
  const { t } = useTranslation();
  const PHONES = usePhones();
  const location = useLocation();
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeAll = () => {
    setMegaOpen(false);
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* Utility bar */}
      <div className="hidden md:block border-b border-white/5 bg-venka-ink/90 backdrop-blur">
        <div className="mx-auto max-w-7xl px-6 flex items-center justify-between h-9 text-[11.5px] text-slate-300">
          <div className="flex items-center gap-5">
            <a href={`tel:${PHONES.angola.tel}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone" title={PHONES.angola.label.en}>
              <Phone className="h-3 w-3 text-venka-green-light" /> <span className="text-slate-500">AO</span> {PHONES.angola.display}
            </a>
            <a href={`tel:${PHONES.intl.tel}`} className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-phone-intl" title={PHONES.intl.label.en}>
              <Phone className="h-3 w-3 text-venka-green-light" /> <span className="text-slate-500">EE · Intl</span> {PHONES.intl.display}
            </a>
            <a href="mailto:sales@venkawe.com" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="topbar-email">
              <Mail className="h-3 w-3 text-venka-green-light" /> sales@venkawe.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/account/login" className="hover:text-white transition-colors" data-testid="topbar-client-login">
              Client Portal
            </Link>
            <span className="text-white/15">|</span>
            <Link to="/portal/login" className="hover:text-white transition-colors" data-testid="topbar-staff-login">
              Staff Login
            </Link>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="glass border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="navbar-logo-link" onClick={closeAll}>
            <img
              src="/venka-logo.png"
              alt="VENKA World Express"
              className="h-12 w-auto drop-shadow-[0_2px_8px_rgba(30,56,224,0.35)]"
              data-testid="navbar-logo-image"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            <NavLink to="/" end className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-home">
              {t("common.nav.home", "Home")}
            </NavLink>

            {/* Services mega menu trigger */}
            <div
              className="relative"
              onMouseEnter={() => setMegaOpen(true)}
              onMouseLeave={() => setMegaOpen(false)}
            >
              <button
                type="button"
                className={`${linkBase} flex items-center gap-1 ${megaOpen ? linkActive : ""}`}
                onClick={() => setMegaOpen(true)}
                data-testid="nav-services-trigger"
              >
                {t("common.nav.services", "Services")}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {megaOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[720px]"
                    data-testid="nav-services-megamenu"
                  >
                    <div className="glass-panel rounded-2xl border border-white/10 shadow-2xl shadow-black/50 overflow-hidden grid grid-cols-3">
                      {/* Feature column */}
                      <div className="col-span-1 relative p-6 brand-gradient text-white">
                        <p className="text-[11px] uppercase tracking-[0.22em] text-white/70">VENKA W.E</p>
                        <h4 className="font-serif text-2xl mt-2 leading-tight">Your journey, handled end to end</h4>
                        <p className="text-xs text-white/80 mt-3 leading-relaxed">
                          From visas to flight tickets — one trusted partner in Angola and beyond.
                        </p>
                        <Button
                          asChild
                          size="sm"
                          className="mt-6 rounded-full bg-white text-venka-blue hover:bg-white/90 text-xs font-semibold"
                          data-testid="megamenu-apply-cta"
                        >
                          <Link to="/apply" onClick={closeAll}>
                            {t("apply.ctaOnline", "Apply Online")} <ArrowRight className="ml-1 h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </div>
                      {/* Services grid */}
                      <div className="col-span-2 p-3 grid grid-cols-2 gap-1 bg-card/60">
                        {SERVICES.map((s) => {
                          const Icon = s.icon;
                          return (
                            <Link
                              key={s.key}
                              to={s.to}
                              onClick={closeAll}
                              className="group flex gap-3 rounded-xl p-3 hover:bg-white/5 transition-colors"
                              data-testid={`megamenu-service-${s.key}`}
                            >
                              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-venka-blue/15 text-venka-green-light group-hover:bg-venka-blue/25 transition-colors">
                                <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
                              </span>
                              <span className="min-w-0">
                                <span className="block text-sm font-semibold text-slate-100 group-hover:text-white">{s.title}</span>
                                <span className="block text-[11.5px] text-slate-400 leading-snug mt-0.5">{s.desc}</span>
                              </span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href="/#destinations" className={linkBase} data-testid="nav-destinations">
              {t("common.nav.destinations", "Destinations")}
            </a>
            <NavLink to="/about" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-about">
              {t("common.nav.about", "About")}
            </NavLink>
            <NavLink to="/packages" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-packages">
              {t("common.nav.packages", "Packages")}
            </NavLink>
            <NavLink to="/blog" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-blog">
              {t("common.nav.blog", "Blog")}
            </NavLink>
            <NavLink to="/contact" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : ""}`} data-testid="nav-contact">
              {t("common.nav.contact", "Contact")}
            </NavLink>
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/contact" aria-label="Contact" data-testid="mobile-contact-icon"
              className="md:hidden grid h-9 w-9 place-items-center rounded-full bg-venka-blue/20 text-venka-green-light">
              <Phone className="h-4 w-4" />
            </Link>
            <LanguageSelector />
            <Button
              asChild
              size="sm"
              className="hidden sm:inline-flex rounded-full brand-gradient text-white hover:opacity-90 px-5 text-xs font-semibold shadow-lg shadow-venka-blue/25"
              data-testid="navbar-cta-apply"
            >
              <Link to="/apply">{t("apply.ctaOnline", "Apply Online")}</Link>
            </Button>
            <button
              type="button"
              className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg text-slate-100 hover:bg-white/10"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden glass-panel border-b border-white/10 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-5 py-4 space-y-1">
              <Link to="/" onClick={closeAll} className="block py-2 text-sm text-slate-200" data-testid="mobile-nav-home">Home</Link>
              <p className="pt-3 pb-1 text-[11px] uppercase tracking-[0.2em] text-venka-green-light">Services</p>
              {SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.key} to={s.to} onClick={closeAll} className="flex items-center gap-3 py-2 text-sm text-slate-200" data-testid={`mobile-service-${s.key}`}>
                    <Icon className="h-4 w-4 text-venka-green-light" /> {s.title}
                  </Link>
                );
              })}
              <a href="/#destinations" onClick={closeAll} className="block py-2 text-sm text-slate-200 mt-2">Destinations</a>
              <Link to="/about" onClick={closeAll} className="block py-2 text-sm text-slate-200" data-testid="mobile-nav-about">About</Link>
              <Link to="/packages" onClick={closeAll} className="block py-2 text-sm text-slate-200" data-testid="mobile-nav-packages">Packages</Link>
              <Link to="/blog" onClick={closeAll} className="block py-2 text-sm text-slate-200" data-testid="mobile-nav-blog">Blog</Link>
              <Link to="/contact" onClick={closeAll} className="block py-2 text-sm text-slate-200" data-testid="mobile-nav-contact">Contact</Link>
              <div className="grid grid-cols-2 gap-2 pt-4">
                <Button asChild className="rounded-full brand-gradient text-white text-xs" data-testid="mobile-apply-cta">
                  <Link to="/apply" onClick={closeAll}>Apply Online</Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-white/20 text-slate-100 text-xs" data-testid="mobile-client-login">
                  <Link to="/account/login" onClick={closeAll}><UserRound className="mr-1 h-3.5 w-3.5" /> Client Portal</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
